/**
 * src/components/ProductNav.js
 *
 * Responsive two-bar header that behaves as ONE system across all widths:
 *
 *   FULL    (vw >= 1560)         - original layout: gradient panels with title
 *                                  + subtitle, full utility row. Covers 22"/24"
 *                                  (1920) and 27" (2560).
 *   COMPACT (1140 <= vw < 1560)  - laptop mode: gradient panels keep their
 *                                  colours but show a single-line title only
 *                                  (no subtitle, no font reduction); utility row
 *                                  drops the greeting and email and tightens
 *                                  spacing so all controls stay on screen.
 *   MOBILE  (vw < 1140)          - hamburger menu. 1140 is the width below which
 *                                  seven title pills can no longer sit on one
 *                                  row without the longest single word
 *                                  overflowing, so the header collapses rather
 *                                  than breaking.
 *
 * Both rows are driven by the same breakpoints, so they transition together.
 * Colours, gradients, fonts, branding, order and Security styling are unchanged.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { PRODUCTS, PRODUCT_ORDER } from '../config/products';
import { CATEGORY_PALETTES } from '../config/categoryTheme';
import '../portalResponsive.css';

// Top category navigation surfaces are ported VERBATIM from the real DCC source
// of truth: src/commandcentre/commandCentre.css --f-<family> (the .f1..f7 top
// category buttons) in the VISINVOKE Desktop Command Centre. Deep two-stop
// family gradients, not the paler portal page palette, so the bar reads with
// full DCC depth and saturation instead of washing toward white.
const NAV_GRADIENTS = {
  visual:        'linear-gradient(135deg, #3a1d6e, #6a34c9)', // DCC --f-dnd-image
  professional:  'linear-gradient(135deg, #123a63, #1f76b8)', // DCC --f-professional
  battlemaps:    'linear-gradient(135deg, #8a4a12, #d1841f)', // DCC --f-battlemaps
  battletracker: 'linear-gradient(135deg, #0f5a3a, #18a06a)', // DCC --f-combat-pro
  gaming:        'linear-gradient(135deg, #5a1020, #a91836)', // DCC --f-rpg-worlds
  security:      'linear-gradient(135deg, #8a6a12, #e0a41a)', // DCC --f-security
  utilities:     'linear-gradient(135deg, #123a8a, #2f6bff)', // DCC --f-utilities
};
const GRADIENTS = PRODUCT_ORDER.reduce((acc, key) => {
  acc[key] = NAV_GRADIENTS[key] || NAV_GRADIENTS.visual;
  return acc;
}, {});

// Category glow used for the selected-panel box-shadow.
const GLOWS = PRODUCT_ORDER.reduce((acc, key) => {
  const pal = CATEGORY_PALETTES[key];
  if (pal) acc[key] = pal.glow;
  return acc;
}, {});

// Vibrant dark glossy glass for the top category buttons and header pills:
// a near-black glossy body with the family colour washed richly through the
// top, a glossy specular sheen, a glowing neon rim and glowing text. This is
// the approved "level 2" material.
function viGlassRgb(hex) {
  var h = (hex || '#A347FF').replace('#', '');
  if (h.length === 3) { h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]; }
  return parseInt(h.substring(0, 2), 16) + ', ' + parseInt(h.substring(2, 4), 16) + ', ' + parseInt(h.substring(4, 6), 16);
}
function viGlassFill(d, p, br) {
  return 'linear-gradient(180deg, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0.08) 30%, rgba(255,255,255,0) 54%), ' +
         'linear-gradient(180deg, rgba(' + viGlassRgb(p) + ',0.72) 0%, rgba(' + viGlassRgb(p) + ',0.30) 44%, rgba(' + viGlassRgb(d) + ',0.16) 74%, rgba(9,11,17,0) 100%), ' +
         'linear-gradient(180deg, #14171f 0%, #090b11 100%)';
}
function viGlassShadow(d, p, br, strong) {
  if (strong) {
    return '0 0 30px rgba(' + viGlassRgb(p) + ',1), 0 0 62px rgba(' + viGlassRgb(p) + ',0.6), inset 0 0 28px rgba(' + viGlassRgb(p) + ',0.4), inset 0 1px 0 rgba(255,255,255,0.3), 0 10px 26px rgba(0,0,0,0.55)';
  }
  return '0 0 20px rgba(' + viGlassRgb(p) + ',0.8), 0 0 46px rgba(' + viGlassRgb(p) + ',0.45), inset 0 0 24px rgba(' + viGlassRgb(p) + ',0.28), inset 0 1px 0 rgba(255,255,255,0.28), 0 10px 26px rgba(0,0,0,0.55)';
}
function viGlassTextGlow(br) {
  return '0 0 10px rgba(' + viGlassRgb(br) + ',0.9), 0 1px 4px rgba(0,0,0,0.6)';
}
function viGlassBorder(br, width) {
  return (width || 1.5) + 'px solid rgba(' + viGlassRgb(br) + ',0.9)';
}
const NAV_GLASS = PRODUCT_ORDER.reduce((acc, key) => {
  const pal = CATEGORY_PALETTES[key] || CATEGORY_PALETTES.visual;
  acc[key] = {
    fill: viGlassFill(pal.deep, pal.primary, pal.bright),
    shadow: viGlassShadow(pal.deep, pal.primary, pal.bright, false),
    shadowActive: viGlassShadow(pal.deep, pal.primary, pal.bright, true),
    textGlow: viGlassTextGlow(pal.bright),
    border: viGlassBorder(pal.bright, 1.5),
    borderActive: '2px solid rgba(' + viGlassRgb(pal.bright) + ',1)',
  };
  return acc;
}, {});

const HIDE_DASHBOARD_ROUTES = [
  '/dashboard', '/dashboard/visual', '/dashboard/professional',
  '/dashboard/battlemaps', '/dashboard/battletracker',
  '/dashboard/battletracker/pricing', '/dashboard/battletracker/membership',
  '/dashboard/battletracker/purchase-history',
  '/dashboard/gaming',
  '/dashboard/rpg', '/dashboard/rpg/pricing',
  '/dashboard/security', '/dashboard/utilities',
];

// Responsive breakpoints (single source of truth for both header rows).
const BP_COMPACT = 1140; // below this -> hamburger
const BP_FULL    = 1560; // at/above this -> full layout

export default function ProductNav() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, logout } = useAuth() || {};
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hovered, setHovered]       = useState(null);

  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440);
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isMobile  = vw < BP_COMPACT;
  const isFull    = vw >= BP_FULL;
  const isCompact = !isMobile && !isFull;

  const displayName  = user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user?.email;
  const avatarLetter = (user?.first_name || user?.email || 'V')[0].toUpperCase();
  const hideDash     = HIDE_DASHBOARD_ROUTES.includes(location.pathname);

  function isActive(p) {
    return location.pathname.startsWith(p.route) ||
      (p.childRoutes && p.childRoutes.some(r => location.pathname.startsWith(r))) ||
      (p.key === 'visual' && (location.pathname === '/dashboard' || location.pathname.startsWith('/generators')));
  }

  function go(p) { setMobileOpen(false); navigate(p.route); }

  const barTopOffset = isMobile ? 64 : undefined; // mobile bar is shorter

  return (
    <div className="vi-portal-nav" style={s.wrapper}>

      {/* ── BAR 2: Utility / account row ────────────────────── */}
      {!isMobile && (
        <div className="vi-portal-util-bar" style={s.utilBar}>
          <div className="vi-portal-util-inner" style={{ ...s.utilBarInner, ...(isCompact ? s.utilBarInnerCompact : {}) }}>

            <span className="visinvoke-logo-text vi-portal-wordmark" style={s.viWordmark} onClick={() => navigate('/dashboard')}>VISINVOKE</span>

            {isFull && (
              <div className="vi-portal-identity" style={s.utilCell}>
                <div style={s.divider} />
                <span style={s.greeting}>Welcome back, {user?.first_name || 'there'}!</span>
              </div>
            )}

            <div className="vi-portal-identity" style={s.utilCell}>
              <div style={s.divider} />
              <div style={s.avatar}>{avatarLetter}</div>
              <span style={s.userName}>{displayName}</span>
              {isFull && <span style={s.userEmail}>{user?.email}</span>}
            </div>

            <div style={s.utilCellRight}>
              <div style={s.divider} />
              <button className="vi-portal-util-action" data-help-id="nav-command-center" style={s.cmdBtn} onMouseEnter={e => e.currentTarget.style.filter='brightness(1.12)'} onMouseLeave={e => e.currentTarget.style.filter=''} onClick={() => navigate('/desktop-command-center')}>{isFull ? 'Get Command Center' : 'Command Center'}</button>
            </div>

            <div style={s.utilCell}>
              <div style={s.divider} />
              <button className="vi-portal-util-action" data-help-id="nav-growth" style={s.growthBtn} onMouseEnter={e => e.currentTarget.style.filter='brightness(1.15)'} onMouseLeave={e => e.currentTarget.style.filter=''} onClick={() => navigate('/dashboard/growth-pricing')}>Growth &amp; Pricing</button>
            </div>
            <div style={s.utilCell}>
              <div style={s.divider} />
              <button className="vi-portal-util-action" data-help-id="nav-generators" style={s.acctBtn} onMouseEnter={e => e.currentTarget.style.filter='brightness(1.15)'} onMouseLeave={e => e.currentTarget.style.filter=''} onClick={() => navigate('/dashboard/standalone-generators')}>Generators</button>
            </div>

            <div style={s.utilCell}>
              <div style={s.divider} />
              <button className="vi-portal-util-action" data-help-id="nav-account" style={s.acctBtn} onMouseEnter={e => e.currentTarget.style.filter='brightness(1.15)'} onMouseLeave={e => e.currentTarget.style.filter=''} onClick={() => navigate('/manage')}>Account</button>
            </div>

            <div style={s.utilCell}>
              <div style={s.divider} />
              <button className="vi-portal-util-action" data-help-id="nav-signout" style={s.outBtn} onMouseEnter={e => e.currentTarget.style.filter='brightness(1.15)'} onMouseLeave={e => e.currentTarget.style.filter=''} onClick={async () => { await logout(); navigate('/login'); }}>Sign Out</button>
            </div>

            {!hideDash && (
              <div style={s.utilCell}>
                <div style={s.divider} />
                <button className="vi-portal-util-action" style={s.dashBtn} onMouseEnter={e => e.currentTarget.style.filter='brightness(1.15)'} onMouseLeave={e => e.currentTarget.style.filter=''} onClick={() => navigate('/dashboard')}>&larr; Dashboard</button>
              </div>
            )}

          </div>
        </div>
      )}

      {/* ── BAR 1: Ecosystem panels ─────────────────────────── */}
      <nav className="vi-portal-category-bar" style={{ ...s.mainBar, ...(isMobile ? s.mainBarMobile : {}) }}>
        <div style={{ ...s.topAccent, ...(isCompact ? s.topAccentCompact : {}) }}>
          {PRODUCT_ORDER.map(key => {
            const pal = CATEGORY_PALETTES[key] || CATEGORY_PALETTES.visual;
            return <span key={`accent-${key}`} style={{ ...s.topAccentSeg, background: pal.primary, boxShadow: '0 0 6px rgba(' + viGlassRgb(pal.primary) + ',0.75)' }} />;
          })}
        </div>

        {!isMobile && (
          <div className="vi-portal-category-row" style={{ ...s.panelRow, ...(isCompact ? s.panelRowCompact : {}) }}>
            {PRODUCT_ORDER.map(key => {
              const p      = PRODUCTS[key];
              const active = isActive(p);
              const hover  = hovered === key && !active;
              return (
                <button
                  className="vi-portal-category-tile"
                  key={key}
                  data-help-id={`nav-${key}`}
                  style={{
                    ...s.panel,
                    ...(isCompact ? s.panelCompact : {}),
                    background: NAV_GLASS[key].fill,
                    border: NAV_GLASS[key].border,
                    boxShadow: NAV_GLASS[key].shadow,
                    ...(active ? { ...s.panelActive, background: NAV_GLASS[key].fill, border: NAV_GLASS[key].borderActive, boxShadow: NAV_GLASS[key].shadowActive } : {}),
                    ...(hover  ? { ...s.panelHover, boxShadow: NAV_GLASS[key].shadowActive } : {}),
                    ...(p.comingSoon ? s.panelComing : {}),
                  }}
                  onClick={() => go(p)}
                  onMouseEnter={() => setHovered(key)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <span style={{ ...s.panelContent, ...(p.navDescLines ? s.panelContentLines : {}) }}>
                    <span className="vi-portal-category-title" style={{ ...(isCompact ? s.panelTitleCompact : s.panelTitle), textShadow: NAV_GLASS[key].textGlow }}>{p.navTitle || p.displayName}</span>
                    {isFull && <span style={{ ...s.panelSub, ...(p.navDescLines ? s.panelSubLines : {}), textShadow: NAV_GLASS[key].textGlow }}>{p.navDescLines
                      ? p.navDescLines.map(function (line, i) { return <span key={i} style={s.panelSubLine}>{line}</span>; })
                      : (p.navDesc || '')}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '0 16px', justifyContent: 'space-between' }}>
            <span className="visinvoke-logo-text" style={s.viWordmarkMobile} onClick={() => navigate('/dashboard')}>VISINVOKE</span>
            <button style={s.hamburger} onClick={() => setMobileOpen(o => !o)}>{mobileOpen ? '✕' : '☰'}</button>
          </div>
        )}

        {mobileOpen && (
          <div style={{ ...s.mobileMenu, top: barTopOffset }}>
            {PRODUCT_ORDER.map(key => {
              const p = PRODUCTS[key]; const active = isActive(p);
              return (
                <button key={key} style={{ ...s.mobileItem, ...(active ? { color: '#fff', background: `${p.color}18`, borderLeft: `3px solid ${p.color}` } : {}) }} onClick={() => go(p)}>
                  <span style={{ fontSize: 18 }}>{p.icon}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{p.navTitle || p.displayName}</div>
                    {p.navDesc && <div style={{ fontSize: 11, opacity: 0.5, marginTop: 2 }}>{p.navDesc}</div>}
                  </div>
                </button>
              );
            })}
            <div style={s.mobileDivider} />
            <button style={s.mobileItem} onClick={() => { setMobileOpen(false); navigate('/desktop-command-center'); }}>Get Command Center</button>
            <button style={s.mobileItem} onClick={() => { setMobileOpen(false); navigate('/dashboard/growth-pricing'); }}>Growth &amp; Pricing</button>
            <button style={s.mobileItem} onClick={() => { setMobileOpen(false); navigate('/dashboard/standalone-generators'); }}>Generators</button>
            <button style={s.mobileItem} onClick={() => { setMobileOpen(false); navigate('/manage'); }}>Account Settings</button>
            <button style={s.mobileItem} onClick={async () => { await logout(); navigate('/login'); }}>Sign Out</button>
          </div>
        )}
      </nav>

    </div>
  );
}

const s = {
  wrapper: {
    position: 'sticky',
    top: 0,
    zIndex: 200,
    display: 'flex',
    flexDirection: 'column',
  },

  /* ── Main panel bar ── */
  mainBar: {
    position: 'relative',
    display: 'flex',
    alignItems: 'stretch',
    height: 'auto',
    minHeight: 104,
    background: 'linear-gradient(180deg, #04070f 0%, #07101e 100%)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    boxShadow: '0 6px 40px rgba(0,0,0,0.75)',
  },

  mainBarMobile: {
    minHeight: 64,
    alignItems: 'center',
  },

  topAccent: {
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 4,
    display: 'flex',
    gap: 5,
    padding: '0 10px',
    background: 'transparent',
    pointerEvents: 'none',
    zIndex: 2,
  },

  topAccentCompact: {
    gap: 4,
    padding: '0 8px',
  },

  topAccentSeg: {
    flex: '1 1 0',
    height: '100%',
    borderRadius: 2,
  },

  panelRow: {
    flex: 1,
    display: 'flex',
    alignItems: 'stretch',
    gap: 5,
    padding: '8px 10px',
  },

  panelRowCompact: {
    gap: 4,
    padding: '6px 8px',
  },

  panel: {
    position: 'relative',
    flex: '1 1 0',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    borderRadius: 11,
    padding: '8px 14px',
    cursor: 'pointer',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.10)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -12px 24px rgba(0,0,0,0.22), 0 4px 18px rgba(0,0,0,0.45)',
    minWidth: 0,
    textAlign: 'center',
    minHeight: 94,
    boxSizing: 'border-box',
  },

  panelCompact: {
    padding: '6px 10px',
    minHeight: 60,
  },

  panelActive: {
    border: '2px solid rgba(255,255,255,0.92)',
    boxShadow: '0 0 0 1px rgba(255,255,255,0.22), 0 8px 22px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.16)',
    transform: 'translateY(-2px)',
  },

  panelHover: {
    transform: 'translateY(-3px)',
    boxShadow: '0 14px 34px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.16)',
    filter: 'saturate(1.08)',
  },

  panelComing: {
    filter: 'saturate(0.9) brightness(0.92)',
  },

  panelContent: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
    flex: 1,
  },

  panelTitle: {
    display: 'block',
    fontWeight: 800,
    fontSize: 22,
    lineHeight: 1.08,
    letterSpacing: '-0.3px',
    color: '#fff',
    textShadow: '0 1px 6px rgba(0,0,0,0.55)',
    width: '100%',
    textAlign: 'center',
    marginBottom: 6,
    flexShrink: 0,
  },

  panelTitleCompact: {
    display: 'block',
    fontWeight: 800,
    fontSize: 22,
    lineHeight: 1.1,
    letterSpacing: '-0.3px',
    color: '#fff',
    textShadow: '0 1px 6px rgba(0,0,0,0.55)',
    width: '100%',
    textAlign: 'center',
    marginBottom: 0,
  },

  // Explicit two-line tile subtitle (products with navDescLines, e.g. Combat PRO):
  // each line never wraps; the size is 15px like every other tile and only scales
  // down if the tile is too narrow for the longer line, so it never clips.
  panelContentLines: {
    containerType: 'inline-size',
  },
  panelSubLines: {
    fontSize: 'min(15px, calc(100cqi / 15.2))',
  },
  panelSubLine: {
    display: 'block',
    whiteSpace: 'nowrap',
  },

  panelSub: {
    display: 'block',
    fontSize: 15,
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: 0,
    color: '#EDF2F9',
    textShadow: '0 1px 6px rgba(0,0,0,0.75)',
    whiteSpace: 'normal',
    textWrap: 'balance',
    width: '100%',
    textAlign: 'center',
  },

  /* ── Utility / account bar ── */
  utilBar: {
    background: 'rgba(3,5,12,0.98)',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    boxShadow: '0 2px 14px rgba(0,0,0,0.45)',
  },

  utilBarInner: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '0 22px',
    minHeight: 50,
    flexWrap: 'wrap',
    boxSizing: 'border-box',
  },

  utilBarInnerCompact: {
    gap: 10,
    padding: '6px 14px',
  },

  viLogo: {
    flexShrink: 0,
    cursor: 'pointer',
    height: 26,
    width: 'auto',
    maxHeight: 26,
    objectFit: 'contain',
    display: 'block',
    userSelect: 'none',
    filter: 'drop-shadow(0 0 8px rgba(167,139,250,0.4))',
  },

  viWordmark: {
    flexShrink: 0,
    cursor: 'pointer',
    color: '#ffffff',
    fontSize: 30,
    fontWeight: 800,
    letterSpacing: 3,
    lineHeight: 1,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    display: 'block',
  },

  viWordmarkMobile: {
    flexShrink: 0,
    cursor: 'pointer',
    color: '#ffffff',
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: 2.5,
    lineHeight: 1,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    display: 'block',
  },

  divider: {
    width: 1,
    height: 22,
    background: 'rgba(255,255,255,0.1)',
    flexShrink: 0,
  },

  utilCell: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 },
  utilCellRight: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, marginLeft: 'auto' },

  greeting: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: 900,
    lineHeight: 1,
    textShadow: '0 1px 8px rgba(0,0,0,0.4)',
    whiteSpace: 'nowrap',
  },

  avatar: {
    width: 31,
    height: 31,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    flexShrink: 0,
  },

  userName:  { color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap' },
  userEmail: { color: '#ffffff', fontSize: 11, lineHeight: 1.2, whiteSpace: 'nowrap' },

  cmdBtn:    { background: viGlassFill('#0A6E92','#18BDF2','#5CE0FF'), border: viGlassBorder('#5CE0FF'), color:'#fff', borderRadius:999, padding:'10px 18px', cursor:'pointer', fontSize:13, fontWeight:800, whiteSpace:'nowrap', transition:'filter 0.15s ease, box-shadow 0.15s ease', flexShrink: 0, boxShadow: viGlassShadow('#0A6E92','#18BDF2','#5CE0FF',false), textShadow: viGlassTextGlow('#5CE0FF') },
  growthBtn: { background: viGlassFill('#6D24C8','#A347FF','#C88BFF'), border: viGlassBorder('#C88BFF'), color:'#fff', borderRadius:999, padding:'10px 18px', cursor:'pointer', fontSize:12, fontWeight:800, whiteSpace:'nowrap', transition:'filter 0.15s', flexShrink: 0, boxShadow: viGlassShadow('#6D24C8','#A347FF','#C88BFF',false), textShadow: viGlassTextGlow('#C88BFF') },
  acctBtn:   { background: viGlassFill('#1749B8','#2F7DFF','#63A6FF'), border: viGlassBorder('#63A6FF'), color:'#fff', borderRadius:999, padding:'10px 18px', cursor:'pointer', fontSize:12, fontWeight:800, whiteSpace:'nowrap', transition:'filter 0.15s', flexShrink: 0, boxShadow: viGlassShadow('#1749B8','#2F7DFF','#63A6FF',false), textShadow: viGlassTextGlow('#63A6FF') },
  outBtn:    { background: viGlassFill('#9A1F2C','#EF445A','#FF7E90'), border: viGlassBorder('#FF7E90'), color:'#fff', borderRadius:999, padding:'10px 18px', cursor:'pointer', fontSize:12, fontWeight:800, whiteSpace:'nowrap', transition:'filter 0.15s', flexShrink: 0, boxShadow: viGlassShadow('#9A1F2C','#EF445A','#FF7E90',false), textShadow: viGlassTextGlow('#FF7E90') },
  dashBtn:   { background: viGlassFill('#334155','#64748B','#94A3B8'), border: viGlassBorder('#94A3B8'), color:'#fff', borderRadius:999, padding:'10px 18px', cursor:'pointer', fontSize:12, fontWeight:700, whiteSpace:'nowrap', transition:'filter 0.15s', flexShrink: 0, boxShadow: viGlassShadow('#334155','#64748B','#94A3B8',false), textShadow: viGlassTextGlow('#94A3B8') },

  /* ── Mobile ── */
  mobileLogo: {
    flexShrink: 0, cursor: 'pointer',
    height: 24, width: 'auto', maxHeight: 24,
    objectFit: 'contain', display: 'block', userSelect: 'none',
  },

  hamburger: { background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', padding: '0 4px' },

  mobileMenu: {
    position: 'absolute', top: 64, left: 0, right: 0,
    background: '#080c18', borderBottom: '1px solid rgba(255,255,255,0.1)',
    display: 'flex', flexDirection: 'column', zIndex: 300,
    padding: '10px 0', boxShadow: '0 16px 48px rgba(0,0,0,0.7)',
    maxHeight: '80vh', overflowY: 'auto',
  },

  mobileItem: {
    display: 'flex', alignItems: 'center', gap: 14,
    background: 'none', border: 'none', borderLeft: '3px solid transparent',
    color: 'rgba(255,255,255,0.72)', padding: '13px 24px',
    cursor: 'pointer', fontSize: 14, fontWeight: 600, textAlign: 'left',
  },

  mobileDivider: { height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 0' },
};
